import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomStyle, setZoomStyle] = useState({ backgroundPosition: 'center', backgroundSize: 'contain' });
  const [avaliacao, setAvaliacao] = useState(0);
  const [totalAvaliacoes, setTotalAvaliacoes] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressData, setAddressData] = useState({
    endereco: 'Retirar no Local',
    transportadora: '',
    transportadoraNumero: '',
  });
  
  const whatsappNumber = '5551983422322'; // Número do WhatsApp

  // Função para verificar a autenticação do usuário
  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await axios.get('https://crmesquita.com.br/api/check-login-status/', {
        headers: { Authorization: `Token ${token}` },
      });
      setIsAuthenticated(response.data.isAuthenticated);
    } catch (error) {
      console.error('Erro ao verificar o estado de login:', error);
      setIsAuthenticated(false);
    }
  };

  // Função para buscar o e-mail do usuário autenticado
  const fetchUserEmail = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get('https://crmesquita.com.br/api/get-user-email/', {
        headers: { Authorization: `Token ${token}` },
      });
      setUserEmail(response.data.email);
    } catch (error) {
      console.error('Erro ao buscar o e-mail do usuário:', error);
    }
  };

  // Função para buscar os detalhes do produto
  const fetchProduct = async () => {
    try {
      const response = await axios.get(`https://crmesquita.com.br/api/produtos/${id}/`);
      setProduct(response.data);
      setSelectedImage(response.data.marca?.imagem || response.data.imagem);
      setAvaliacao(response.data.mediaAvaliacao || 0);
      setTotalAvaliacoes(response.data.totalAvaliacoes || 0);
    } catch (error) {
      console.error('Erro ao buscar detalhes do produto:', error);
    }
  };

  // Formatar preço no padrão brasileiro
  const formatarPreco = (preco) => {
    const precoFloat = parseFloat(preco);
    if (isNaN(precoFloat)) return 'Preço inválido';

    return precoFloat.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  };

  // Adicionar produto ao carrinho
  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existingProductIndex = cart.findIndex((item) => item.id === product.id);

    if (existingProductIndex !== -1) {
      cart[existingProductIndex].quantidade += 1;
    } else {
      cart.push({
        id: product.id,
        nome: product.nome,
        preco: product.preco,
        imagem: selectedImage,
        quantidade: 1,
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    alert('Produto adicionado ao carrinho!');
  };

  // Exibir modal de endereço
  const handleBuy = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowAddressForm(true);
  };

  // Fechar modal de endereço
  const handleCloseAddressForm = () => {
    setShowAddressForm(false);
  };

  // Confirmar endereço e enviar dados ao backend
  const handleConfirmAddress = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("Você precisa estar autenticado para realizar a compra.");
      navigate('/login');
      return;
    }

    // Validação das opções de entrega
    if (
      addressData.endereco === 'Transportadora' &&
      (!addressData.transportadora || !addressData.transportadoraNumero)
    ) {
      alert('Preencha os dados da transportadora.');
      return;
    }

    const requestData = {
      productId: product.id,
      productName: product.nome,
      productPrice: product.preco,
      quantity: 1,
      userEmail: userEmail,
      endereco: addressData.endereco, // Apenas para o backend, não para o Mercado Pago
    };

    console.log("Dados enviados para o backend:", requestData);

    try {
      const response = await axios.post(
        'https://crmesquita.com.br/api/createPreference/',
        requestData,
        {
          headers: { Authorization: `Token ${token}` },
        }
      );

      const { init_point } = response.data;
      if (init_point) {
        // Redirecionar para o link de pagamento gerado pelo Mercado Pago
        window.location.href = init_point;
      } else {
        alert('Erro ao gerar o link de pagamento.');
      }
    } catch (error) {
      console.error('Erro ao processar a compra:', error.response || error.message);
      alert('Erro ao processar a compra. Tente novamente.');
    }
  };

  // Atualizar o estado do formulário de endereço
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData({ ...addressData, [name]: value });
  };

  const handleBuyViaWhatsApp = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const message = `Olá, sou o cliente com o e-mail ${userEmail}. Gostaria de comprar o produto "${product.nome}" pelo valor de ${formatarPreco(product.preco)}.`;
    const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappLink, '_blank');
  };

  // Efeito para carregar dados iniciais
  useEffect(() => {
    fetchProduct();
    checkAuthentication();
    fetchUserEmail();
  }, [id]);

  // Retorno enquanto o produto está sendo carregado
  if (!product) {
    return <p>Carregando...</p>;
  }

  return (
    <div className="product-details-container">
      {/* Seção de imagens */}
      <div className="product-gallery">
        <div
          className="product-main-image"
          onMouseMove={(e) => {
            const { left, top, width, height } = e.target.getBoundingClientRect();
            const x = ((e.clientX - left) / width) * 100;
            const y = ((e.clientY - top) / height) * 100;
            setZoomStyle({ backgroundPosition: `${x}% ${y}%`, backgroundSize: '200%' });
          }}
          onMouseLeave={() =>
            setZoomStyle({ backgroundPosition: 'center', backgroundSize: 'contain' })
          }
          style={{
            backgroundImage: `url(${selectedImage})`,
            ...zoomStyle,
          }}
        >
          <img src={selectedImage} alt={product.nome} className="product-image" />
        </div>
        <div className="product-thumbnails">
          {[product.marca?.imagem, ...(product.imagens || []).map((img) => img.imagem)].map(
            (image, index) => (
              <img
                key={index}
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className={`thumbnail-image ${selectedImage === image ? 'active' : ''}`}
                onClick={() => setSelectedImage(image)}
              />
            )
          )}
        </div>
      </div>

      {/* Informações do produto */}
      <div className="product-info">
        <h1 className="product-title">{product.marca?.nome} | {product.nome}</h1>
        <p className="product-availability">
          Disponibilidade: {product.estoque > 0 ? 'Em estoque' : 'Indisponível'}
        </p>
        {product.estoque <= 1 && (
          <p style={{ color: 'red' }}>Estoque crítico: apenas {product.estoque} unidade(s) restante(s).</p>
        )}
        <p className="product-description">{product.descricao}</p>
        <p className="product-price">{formatarPreco(product.preco)}</p>

        {/* Botões */}
        <button className="add-to-cart-button" onClick={handleAddToCart} disabled={product.estoque === 0}>
          Adicionar ao Carrinho
        </button>
        <button className="buy-button" onClick={handleBuy}>
          Comprar
        </button>
        <button className="whatsapp-button" onClick={handleBuyViaWhatsApp}>
          Comprar pelo WhatsApp
        </button>
      </div>

      {/* Modal de endereço */}
      {showAddressForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Confirme seu Endereço</h2>
            <label>
              Opção de Entrega:
              <select
                name="endereco"
                value={addressData.endereco}
                onChange={handleAddressChange}
              >
                <option value="Retirar no Local">Retirar no Local</option>
                <option value="Transportadora">Transportadora</option>
              </select>
            </label>
            {addressData.endereco === 'Transportadora' && (
              <>
                <label>
                  Nome da Transportadora:
                  <input
                    type="text"
                    name="transportadora"
                    value={addressData.transportadora}
                    onChange={handleAddressChange}
                  />
                </label>
                <label>
                  Número de Contato:
                  <input
                    type="text"
                    name="transportadoraNumero"
                    value={addressData.transportadoraNumero}
                    onChange={handleAddressChange}
                  />
                </label>
              </>
            )}
            <button onClick={handleConfirmAddress}>Confirmar</button>
            <button onClick={handleCloseAddressForm}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;